import React, { useEffect, useState } from 'react';
import { Navigate, useNavigate, useSearchParams } from 'react-router-dom';
import { FaArrowLeft, FaChevronLeft, FaChevronRight } from 'react-icons/fa';
import { toast } from 'react-toastify';
import { userAppData } from '../MainApp.js';
import { getAvailableForms, getFormResponse, getMyFormResponses } from './formsUserApi';
import Header from '../common/header';
import FormResponseAnswerValue from './FormResponseAnswerValue.js';
import FormProcessTracker from './FormProcessTracker.js';
import './forms.css';

const TOAST_CONFIG = { autoClose: 4000, position: 'top-right' };

/** Formats a stored UTC timestamp for the current browser locale. */
function formatSubmittedAt(value) {
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? String(value || 'Unknown') : date.toLocaleString('en-GB');
}

/** Converts a stored processing status into a user-facing label. */
function formatStatus(value, workflowStatus, currentStepName) {
    if (workflowStatus === 'declined') return 'Declined';
    if (workflowStatus === 'complete') return 'Complete';
    if (workflowStatus === 'in_progress') return currentStepName ? `Awaiting ${currentStepName}` : 'In progress';
    const labels = {
        submitted: 'Submitted',
        awaiting_file_scan: 'Files scanning',
        file_scan_failed: 'File scan failed',
    };
    return labels[String(value || '').toLowerCase()] || 'Submitted';
}

/** Displays server-paginated Forms responses submitted by the authenticated user. */
export default function MyFormResponsesPage() {
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const authorised = Number(userAppData.formsData?.perms ?? 0) === 1;
    const [responses, setResponses] = useState([]);
    const [formOptions, setFormOptions] = useState([]);
    const [formFilter, setFormFilter] = useState(() => String(searchParams.get('formId') || '').trim());
    const [page, setPage] = useState(1);
    const [pageCursors, setPageCursors] = useState(['']);
    const [nextCursor, setNextCursor] = useState('');
    const [loading, setLoading] = useState(true);
    const [selectedResponse, setSelectedResponse] = useState(null);
    const [detailLoading, setDetailLoading] = useState(false);

    useEffect(() => {
        if (!authorised) return;
        getAvailableForms()
            .then((forms) => setFormOptions([...forms].sort((left, right) => String(left.name || '').localeCompare(String(right.name || '')))))
            .catch((error) => console.error('Failed to load Forms filter options', error));
    }, [authorised]);

    useEffect(() => {
        if (!authorised) return;
        loadPage(1, '');
        // Changing form resets the opaque cursor chain to its first page.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [authorised, formFilter]);

    /** Loads one page using a cursor previously issued for this authenticated query. */
    async function loadPage(targetPage, cursor) {
        setLoading(true);
        try {
            const result = await getMyFormResponses(cursor, formFilter);
            setResponses(result.responses);
            setNextCursor(result.nextCursor);
            setPage(targetPage);
            if (targetPage === 1) setPageCursors(['']);
        } catch (error) {
            console.error('Failed to load submitted Forms responses', error);
            toast.error('❌ Failed to load your submitted forms. Please try again.', TOAST_CONFIG);
        } finally {
            setLoading(false);
        }
    }

    /** Loads the full answer snapshot after the backend verifies ownership. */
    async function openResponse(response) {
        setSelectedResponse(null);
        setDetailLoading(true);
        try {
            setSelectedResponse(await getFormResponse(response.formId, response.submissionId, response.submittedAt));
        } catch (error) {
            console.error('Failed to load submitted Forms response detail', error);
            toast.error('❌ Failed to load this response. Please try again.', TOAST_CONFIG);
        } finally {
            setDetailLoading(false);
        }
    }

    /** Advances using the continuation cursor returned for the current page. */
    function nextPage() {
        if (!nextCursor || loading) return;
        const nextPageNumber = page + 1;
        setPageCursors((current) => {
            const updated = [...current];
            updated[nextPageNumber - 1] = nextCursor;
            return updated;
        });
        loadPage(nextPageNumber, nextCursor);
    }

    /** Returns to a previously visited server page using its stored cursor. */
    function previousPage() {
        if (page <= 1 || loading) return;
        const previousPageNumber = page - 1;
        loadPage(previousPageNumber, pageCursors[previousPageNumber - 1] || '');
    }

    /** Updates the selected form and keeps the page URL shareable/bookmarkable. */
    function handleFormFilterChange(event) {
        const nextFormId = event.target.value;
        setFormFilter(nextFormId);
        setSearchParams(nextFormId ? { formId: nextFormId } : {});
    }

    if (!authorised) return <Navigate to="/app" replace />;

    const selectedFormName = formOptions.find((form) => String(form.formId) === formFilter)?.name;
    const headerTitle = selectedFormName || (formFilter ? 'Form responses' : 'My form responses');

    return <div className="main_div"><main className="internal_main_div forms-responses-page-shell forms-my-responses-page">
        <Header title={headerTitle} subtitle={formFilter ? 'My responses' : 'Previously submitted forms'} rightContent={<button type="button" className="module-page-header__button" onClick={() => navigate('/app/forms')}><FaArrowLeft /> Back to Forms</button>} />
        <section className="forms-responses-page-card">
            <div className="forms-responses-body">
                <label className="forms-my-responses-filter"><span>Form</span><select value={formFilter} onChange={handleFormFilterChange}><option value="">All forms</option>{formOptions.map((form) => <option key={form.formId} value={form.formId}>{form.name || 'Unnamed form'}</option>)}</select></label>
                {loading && <p>Loading your responses...</p>}
                {!loading && responses.length === 0 && <p className="forms-responses-filter-empty">You have not submitted any matching forms yet.</p>}
                {!loading && responses.length > 0 && <div className="forms-responses-table-scroll"><table className="forms-responses-table"><thead><tr><th>Response</th><th>Form</th><th>Submitted</th><th>Status</th><th>Actioned</th></tr></thead><tbody>
                    {responses.map((response) => <tr key={response.submissionId} tabIndex={0} onClick={() => openResponse(response)} onKeyDown={(event) => { if (event.key === 'Enter' || event.key === ' ') { event.preventDefault(); openResponse(response); } }}>
                        <td data-label="Response">{response.responseId > 0 ? `#${response.responseId}` : '—'}</td>
                        <td data-label="Form">{response.formName || 'Unnamed form'}</td>
                        <td data-label="Submitted">{formatSubmittedAt(response.submittedAt)}</td>
                        <td data-label="Status">{response.isArchived ? 'Archived' : formatStatus(response.status, response.workflowStatus, response.currentStepName)}</td>
                        <td data-label="Actioned"><div className="forms-response-status-group"><span className={`forms-response-status ${response.actioned ? 'forms-response-status--yes' : ''}`}>{response.actioned ? 'Yes' : 'No'}</span>{response.isArchived && <span className="forms-response-status forms-response-status--archived">Archived</span>}</div></td>
                    </tr>)}
                </tbody></table></div>}
                {!loading && (page > 1 || nextCursor) && <nav className="forms-responses-pagination" aria-label="My response pages"><button type="button" disabled={page === 1 || loading} onClick={previousPage} aria-label="Previous page"><FaChevronLeft /></button><span>Page {page}</span><button type="button" disabled={!nextCursor || loading} onClick={nextPage} aria-label="Next page"><FaChevronRight /></button></nav>}
            </div>
        </section>
        {(detailLoading || selectedResponse) && <div className="forms-response-detail-layer forms-response-detail-layer--single" role="dialog" aria-modal="true" aria-label="Submitted form response detail"><section className="forms-response-detail">
            <button type="button" className="forms-modal-close" onClick={() => setSelectedResponse(null)} aria-label="Close response">×</button>
            {detailLoading && <div className="forms-response-detail-loading">Loading response...</div>}
            {!detailLoading && selectedResponse && <><header className="forms-response-detail-header"><h2>{selectedResponse.formName}{selectedResponse.responseId > 0 ? ` · Response #${selectedResponse.responseId}` : ''}</h2><p>Submitted {formatSubmittedAt(selectedResponse.submittedAt)}{selectedResponse.isArchived ? ' · Archived' : ''}</p></header><div className="forms-response-answers"><FormProcessTracker workflow={selectedResponse.workflow} submissionId={selectedResponse.submissionId} />{selectedResponse.answers.map((answer, index) => <article key={answer.questionId || index}><h3>{answer.label || `Question ${index + 1}`}</h3><div><FormResponseAnswerValue answer={answer} submissionId={selectedResponse.submissionId} /></div></article>)}</div></>}
        </section></div>}
    </main></div>;
}
